import React, {useCallback, useEffect} from 'react'
import {AdminDocument, FrontEndNotification} from 'common'
import {useFirebase, useSession} from 'components/Firebase/hooks'
import {createContext, useState} from 'react'
import {adminRole} from 'common/enums'
import {usePostgreSQL} from 'lib/api/featureFlag'
import {adminsApi} from 'lib/api'
import {clearPendingAdminSubmission} from 'components/RoleManagementAccess/pendingSubmission'
import {detachBrowserPushSubscription} from 'lib/push/browserPush'

export interface IAppContext {
  appState: {
    role?: adminRole
    isMSAAdmin: boolean
    isAuthorized: boolean
    isSuperAdmin: boolean
    isSuperManager: boolean
    adminDetails?: AdminDocument
    accountsList?: Array<string>
    hasMoreNotifications: boolean
    notifications?: Array<FrontEndNotification>
    lastVisibleNotification?: unknown
  }
  newAppState: (_e: {
    isMSAAdmin?: boolean
    isAuthorized?: boolean
    isSuperAdmin?: boolean
    isSuperManager?: boolean
    hasMoreNotifications?: boolean
  }) => void
  clearPushAndSignOut: () => Promise<void>
  syncPushSubscription: () => Promise<void>
}

const AppContextInitialState: IAppContext = {
  appState: {
    isMSAAdmin: false,
    notifications: [],
    isAuthorized: false,
    isSuperAdmin: false,
    isSuperManager: false,
    hasMoreNotifications: false,
    lastVisibleNotification: undefined
  },
  newAppState: (_e: {
    isMSAAdmin?: boolean
    isAuthorized?: boolean
    isSuperAdmin?: boolean
    isSuperManager?: boolean
    hasMoreNotifications?: boolean
  }) => {},
  clearPushAndSignOut: async () => {},
  syncPushSubscription: async () => {}
}

const AppContext = createContext(AppContextInitialState)

type ResolvedAdminClaims = {
  role?: adminRole
  accountsList: Array<string>
  isSuperAdmin: boolean
  isSuperManager: boolean
  isMSAAdmin: boolean
}

const AppStateProvider = ({children}: {children: React.ReactNode}) => {
  const isPostgres = usePostgreSQL()
  const [appState, setAppState] = useState({...AppContextInitialState.appState})
  const {user} = useSession()
  const userId = user?.uid
  const {
    requestNotifactionsPermission,
    signOut,
    getNotifications,
    getAdminDocument
  } = useFirebase()

  const resolveAdminClaims =
    useCallback(async (): Promise<ResolvedAdminClaims> => {
      if (!user) {
        return {
          role: undefined,
          accountsList: [],
          isSuperAdmin: false,
          isSuperManager: false,
          isMSAAdmin: false
        }
      }

      const idTokenResult = await user.getIdTokenResult()
      const claims = idTokenResult?.claims || {}
      const accountsList = Array.isArray(claims.accounts)
        ? claims.accounts.map(String)
        : []
      const isSuperAdmin = Boolean(claims.superAdmin)
      const isSuperManager = Boolean(claims.superManager)
      const isMSAAdmin = accountsList.length > 0
      const role =
        (isSuperAdmin && adminRole.superAdmin) ||
        (isSuperManager && adminRole.superManager) ||
        (isMSAAdmin && adminRole.MSA) ||
        undefined

      return {
        role,
        accountsList,
        isSuperAdmin,
        isSuperManager,
        isMSAAdmin
      }
    }, [user])

  useEffect(() => {
    if (!user) return
    const unSubscribe = getNotifications(setAppState)
    return () => unSubscribe?.()
  }, [user, userId, isPostgres, getNotifications])

  useEffect(() => {
    if (!userId) return
    let cancelled = false

    const hydrateClaimsAndPush = async () => {
      try {
        const claims = await resolveAdminClaims()
        if (cancelled) return
        setAppState((prev) => ({
          ...prev,
          role: claims.role,
          accountsList: claims.accountsList,
          isSuperAdmin: claims.isSuperAdmin,
          isSuperManager: claims.isSuperManager,
          isMSAAdmin: claims.isMSAAdmin
        }))
        if (claims.role) {
          await requestNotifactionsPermission({role: claims.role})
        }
      } catch (error) {
        console.error('[PostgreSQL] Error hydrating admin claims:', error)
      }
    }

    void hydrateClaimsAndPush()

    return () => {
      cancelled = true
    }
  }, [userId, isPostgres, requestNotifactionsPermission, resolveAdminClaims])

  useEffect(() => {
    if (!userId) return
    if (isPostgres) {
      // PostgreSQL path: fetch admin document from API
      const fetchAdminDoc = async () => {
        try {
          const result = await adminsApi.getCurrent<AdminDocument>()
          setAppState((prev: any) => ({...prev, adminDetails: result}))
        } catch (err) {
          console.error('[PostgreSQL] Error fetching admin document:', err)
        }
      }
      fetchAdminDoc()
      return
    }
    const {unsubscribe} = getAdminDocument(setAppState)
    return () => unsubscribe?.()
  }, [userId, isPostgres, getAdminDocument])

  const syncPushSubscription = async () => {
    const claims = await resolveAdminClaims()
    if (!claims.role) return
    await requestNotifactionsPermission({role: claims.role})
  }

  const newAppState = (appState: Partial<IAppContext['appState']>) =>
    setAppState((prev) => ({...prev, ...appState}))

  const clearPushAndSignOut = async () => {
    setAppState({...AppContextInitialState.appState})
    clearPendingAdminSubmission(userId)
    localStorage.removeItem('hideNotificationBanner')
    const endpoint = await detachBrowserPushSubscription().catch(
      () => undefined
    )
    const firebaseSignOut = signOut()
    const cleanup = endpoint
      ? adminsApi.clearPushSubscription(endpoint).catch(() => undefined)
      : Promise.resolve()
    try {
      await firebaseSignOut
    } finally {
      await cleanup
    }
  }

  return (
    <AppContext.Provider
      value={{appState, newAppState, clearPushAndSignOut, syncPushSubscription}}
    >
      {children}
    </AppContext.Provider>
  )
}

export {AppStateProvider, AppContext}
